import { RawDraftContentState } from 'draft-js';

import { OpenFieldSection, SearchFieldSection } from '../../../datasets/instances/store/types';
import { DatasetType, DefinitionHierarchy, DocumentSpecificHiddenFields, HiddenFields } from '../../../datasets/store/types';
import { SearchFilter } from '../../search/store/types';

export enum DocumentReportingActionTypes {
    SELECT_REPORT_PAGE = 'SELECT_REPORT_PAGE',
    SELECT_WIZARD_TAB = 'SELECT_DOCUMENT_REPORT_WIZARD_TAB',
    UPDATE_DOCUMENT_REPORT_FILTER = 'UPDATE_DOCUMENT_REPORT_FILTER',
    UPDATE_REPORT_DATE_FILTER = 'UPDATE_REPORT_DATE_FILTER',
    FETCH_AVAILABLE_FIELDS_STARTED = 'FETCH_AVAILABLE_FIELDS_STARTED',
    FETCH_AVAILABLE_DOCUMENT_FIELDS_SUCCESSFUL = 'FETCH_AVAILABLE_DOCUMENT_FIELDS_SUCCESSFUL',
    FETCH_AVAILABLE_FIELDS_FAILED = 'FETCH_AVAILABLE_FIELDS_FAILED',
    ADD_DOCUMENT_REPORT_FIELD = 'ADD_DOCUMENT_REPORT_FIELD',
    REMOVE_DOCUMENT_REPORT_FIELD = 'REMOVE_DOCUMENT_REPORT_FIELD',
    RESET_REPORT = 'RESET_REPORT',
    UPDATE_DOCUMENT_REPORT_FIELDS = 'UPDATE_DOCUMENT_REPORT_FIELDS',
    TOGGLE_GENERATE_REPORT_MODAL = 'TOGGLE_GENERATE_REPORT_MODAL',
    GENERATE_REPORT_STARTED = 'GENERATE_REPORT_STARTED',
    GENERATE_REPORT_SUCCESSFUL = 'GENERATE_REPORT_SUCCESSFUL',
    GENERATE_REPORT_FAILED = 'GENERATE_REPORT_FAILED',
    SET_REPORT_UPDATED = 'SET_DOCUMENT_REPORT_UPDATED',
    TOGGLE_REPORT_CONFIRMATION_MODAL = 'TOGGLE_REPORT_CONFIRMATION_MODAL',
    TOGGLE_SAVE_TEMPLATE_MODAL_OPEN = 'TOGGLE_SAVE_TEMPLATE_MODAL_OPEN',
    UPDATE_REPORT_TEMPLATE_VALUE = 'UPDATE_REPORT_TEMPLATE_VALUE',
    SAVE_REPORT_TEMPLATE_STARTED = 'SAVE_REPORT_TEMPLATE_STARTED',
    SAVE_REPORT_TEMPLATE_SUCCESSFUL = 'SAVE_REPORT_TEMPLATE_SUCCESSFUL',
    SAVE_REPORT_TEMPLATE_FAILED = 'SAVE_REPORT_TEMPLATE_FAILED',
    FETCH_ALL_REPORT_TEMPLATES_STARTED = 'FETCH_ALL_REPORT_TEMPLATES_STARTED',
    FETCH_ALL_REPORT_TEMPLATES_SUCCESSFUL = 'FETCH_ALL_REPORT_TEMPLATES_SUCCESSFUL',
    FETCH_ALL_REPORT_TEMPLATES_FAILED = 'FETCH_ALL_REPORT_TEMPLATES_FAILED',
    OPEN_DOCUMENT_REPORT_TEMPLATE = 'OPEN_DOCUMENT_REPORT_TEMPLATE',
    DELETE_REPORT_TEMPLATE_STARTED = 'DELETE_REPORT_TEMPLATE_STARTED',
    DELETE_REPORT_TEMPLATE_FAILED = 'DELETE_REPORT_TEMPLATE_FAILED',
    DELETE_REPORT_TEMPLATE_SUCCESSFUL = 'DELETE_REPORT_TEMPLATE_SUCCESSFUL',
    UPDATE_REPORT_DOCUMENT_NAME_IDS = 'UPDATE_REPORT_DOCUMENT_NAME_IDS',
    UPDATE_REPORT_AGREEMENT_TYPES = 'UPDATE_REPORT_AGREEMENT_TYPES',
    SET_REPORT_AGREEMENT_TYPES = 'SET_REPORT_AGREEMENT_TYPES',
    UPDATE_REPORT_SELECTED_AGREEMENT_TYPE = 'UPDATE_REPORT_SELECTED_AGREEMENT_TYPE',
    SET_REPORT_TEMPLATE_TO_DELETE = 'SET_REPORT_TEMPLATE_TO_DELETE',
    RESET_REPORT_FILTERS = 'RESET_REPORT_FILTERS',
    REMOVE_REPORT_DATASETS_OPEN_FIELD_SECTION = 'REMOVE_REPORT_DATASETS_OPEN_FIELD_SECTION',
    ADD_REPORT_DATASETS_OPEN_FIELD_SECTION = 'ADD_REPORT_DATASETS_OPEN_FIELD_SECTION',
    REMOVE_ALL_REPORT_DATASETS_OPEN_FIELD_SECTIONS = 'REMOVE_ALL_REPORT_DATASETS_OPEN_FIELD_SECTIONS',
    EXPAND_ALL_REPORT_DATASETS_OPEN_FIELD_SECTIONS = 'EXPAND_ALL_REPORT_OPEN_FIELD_SECTIONS',
    CALCULATE_ALL_REPORT_SEARCH_FIELDS = 'CALCULATE_ALL_REPORT_SEARCH_FIELDS',
    SET_ALL_DATASET_REPORT_SEARCH_FIELD_SECTIONS = 'SET_ALL_DATASET_REPORT_SEARCH_FIELD_SECTIONS',
    SET_REPORT_SEARCH_FIELD_SECTION = 'SET_REPORT_SEARCH_FIELD_SECTION',
    SET_REPORT_DATASETS_FUZZY_MATCH_SEARCH_VALUE = 'SET_REPORT_DATASETS_FUZZY_MATCH_SEARCH_VALUE',
    SET_REPORT_DATASETS_SEARCH_FUZZY_MATCHES = 'SET_REPORT_DATASETS_SEARCH_FUZZY_MATCHES',
    RESET_REPORT_SEARCH_FIELD_SECTIONS = 'RESET_REPORT_SEARCH_FIELD_SECTIONS',
    SET_DOCUMENT_REPORT_META_FIELDS = 'SET_DOCUMENT_REPORT_META_FIELDS',
    UPDATE_INSTANCE_REPORT_FIELD_CUSTOM_LABEL = 'UPDATE_INSTANCE_REPORT_FIELD_CUSTOM_LABEL',
    UPDATE_INSTANCE_REPORT_FIELD_INCLUDE_DATASET_TITLE = 'UPDATE_INSTANCE_REPORT_FIELD_INCLUDE_DATASET_TITLE'
}

export enum ReportPage {
    SELECT = 'Select',
    WIZARD = 'Wizard',
    TEMPLATES = 'Templates'
}

export enum ReportTab {
    SELECT = 'Select',
    META_FIELDS = 'Document Information',
    FIELDS = 'Choose Fields',
    CONFIGURE = 'Configure Columns',
    FILTER = 'Filter & Confirm'
}

export enum DocumentMetaReportField {
    DESCRIPTION = 'Original File Name',
    LATEST_AMENDMENT_DESCRIPTION = 'Latest Amendment File Name',
    AGREEMENT_TYPE = 'Agreement Type',
    DOCUMENT_NAME = 'Document Type',
    ENTITY_A = 'Party A',
    ENTITY_B = 'Party B',
    EXECUTED_DATE = 'Date of Execution',
    DATED_AS_OF_DATE = 'Dated as of date',
    CREATED_BY = 'Uploading User',
    LAST_MODIFIED_BY = 'Recently modified User',
    AGENCY = 'Agency Document?',
    EXTRACTED_WITH_AI = 'Extracted with AI?',
    INSTANCE_COMPLETE = 'Latest Instance Published?',
    NETTING_SUMMARY = 'Netting Analysis Summary'
}

export interface DocumentReportField {
    id: string;
    label: string;
    description: string;
    datasetId: number;
    datasetTitle: string;
    datasetType: DatasetType;
    parentFieldId: string;
    order?: number;
    fieldName: string;
    customLabel: string;
    includeDatasetTitle: boolean;
}

export interface AgreementTypeReportFields {
    [agreementTypeId: string]: DocumentReportField[];
}

export interface DocumentReportTemplate {
    reportTemplateId: number;
    name: string;
    description: string;
    reportFields: AgreementTypeReportFields;
    metaFields: DocumentMetaReportField[];
    filters: SearchFilter;
    createdBy: number;
    createdDate: string;
    modifiedBy: number;
    modifiedByName: string;
    modifiedDate: string;
    clientId: number;
}

export interface UpsertDocumentReportTemplate {
    name: string;
    description: string;
    reportFields: AgreementTypeReportFields;
    metaFields: DocumentMetaReportField[];
    reportTemplateId?: number;
    isOpinionReport: boolean;
    filters: SearchFilter;
}

export interface DocumentReportingState {
    reportPage: ReportPage;
    selectedTab: ReportTab;
    fetchingAvailableFields: boolean;
    fetchingError: string | null;
    selectedAgreementTypeId: number | null;
    reportFields: AgreementTypeReportFields;
    generatingReport: boolean;
    reportFilters: SearchFilter;
    reportUpdated: boolean;
    backConfirmationModalOpen: boolean;
    reportTemplates: DocumentReportTemplate[];
    saveTemplateModalOpen: boolean;
    generateModalOpen: boolean;
    templateName: string;
    templateDescription: string;
    templateUserIds: number[];
    templateEmailBody: RawDraftContentState | null;
    saveTemplateError: string | null;
    selectedTemplate: DocumentReportTemplate | null;
    isDeleting: boolean;
    confirmDeleteReportTemplate: number | null;
    fetchingTemplates: boolean;
    definitionHierarchy: DefinitionHierarchy[];
    openFieldsAndSections: OpenFieldSection[];
    clientHiddenFields: HiddenFields;
    documentHiddenFields: DocumentSpecificHiddenFields;
    allSearchFieldSections: SearchFieldSection[];
    searchFuzzyMatches: SearchFieldSection[];
    searchFieldSection: SearchFieldSection | null;
    fieldSectionSearch: string;
    metaFields: DocumentMetaReportField[];
}
